import { ReactNode, useState } from "react";
import moment from "moment";

import { ButtonControl } from "@/Common/Components/Buttons/ButtonControl";
import { HyperLinkControl } from "@/Common/Components/Buttons/HyperLinkControl";
import { CheckboxControl } from "@/Common/Components/Inputs/CheckboxControl";
import { Combobox, ComboBoxDropDownItem } from "@/Common/Components/Inputs/Combobox";
import { TextAreaResizable } from "@/Common/Components/Inputs/TextAreaResizable";
import { TextboxControl } from "@/Common/Components/Inputs/TextboxControl";
import { TextboxControlWithDatePicker } from "@/Common/Components/Inputs/TextboxControlWithDatePicker";
import { useTranslation } from "@/Common/CustomHooks/useTranslation";
import { DateTime } from "@/Common/Helpers/DateTime";
import { DateTimeWithPrecision } from "@/Common/Models/DateTimeWithPrecision";
import { LifeStageDatePrecisionEnum } from "@/Common/Models/LifeStageDatePrecisionEnum";
import { PatientMedicalItemsHelper } from "@/Patient/Helpers/PatientMedicalItemsHelper";
import { MedicationDosageDisplayModeDurationUnit, MedicationModel } from "@/Patient/Models/Medication/MedicationModel";
import { LongTermMedicationIcon } from "./LongTermMedicationIcon";

import styles from "./MedicationEditForm.module.scss";

import img_icon_delete from "@/Globals/_images/icons/delete.png";

interface IMedicationEditFormProps {
    children?: ReactNode;
    medicationModel: MedicationModel;
    routes: Immutable.List<ComboBoxDropDownItem>;
    estimateEndDate: Date;
    totalDurationNumberOfDays: number;
    numberOfRefill: string;
    requiresTotalDuration: boolean;
    currentIdx: number;
    isPreviewButtonVisible?: boolean;
    isDeletable?: boolean;
    fixTextSelectionInterferingWithCheckbox?: boolean;
    onMedicationNameChange: (newText: string) => void;
    onLongTermChange: (isChecked: boolean, id: string, target?: number) => void;
    onDoNotSubstituteChange: (isChecked: boolean, id: string, target?: number) => void;
    onRouteChange: (selectedCode: string) => void;
    onInstructionChange: (newText: string) => void;
    onInternalNoteChange?: (newText: string) => void;
    onAddDosageClick: () => void;
    onInitialRefillQuantityChange: (newText: string) => void;
    onQuantityOrDurationChange: (newText: string) => void;
    onRenewalChange: (newText: string) => void;
    onRefillQuantityChange: (newText: string) => void;
    onTotalDurationChange: (newText: string) => void;
    onStartDateChange: (startDate: Date, startDatePrecision: LifeStageDatePrecisionEnum) => void;
    onToggleEditPreviewMedicationModelClick?: () => void;
    onRemoveMedicationClick?: () => void;
}

interface DateChangeResult {
    date: Date | null;
    datePrecision: LifeStageDatePrecisionEnum | null;
    dateText: string;
}

const onDateChange = (dateWithPrecision: DateTimeWithPrecision, dateTextInput: string): DateChangeResult => {
    const precision = dateWithPrecision.precision ?? LifeStageDatePrecisionEnum.Day;
    let date = null;
    let datePrecision = null;
    let dateText = dateTextInput;

    if (dateWithPrecision.value) {
        date = moment(dateWithPrecision.value).toDate();

        if (precision == LifeStageDatePrecisionEnum.DayApprox || precision == LifeStageDatePrecisionEnum.Day) {
            datePrecision = LifeStageDatePrecisionEnum.Day;
            dateText = moment(dateWithPrecision.value).format("YYYY-MM-DD");
        }
        else {
            datePrecision = precision;
            dateText = PatientMedicalItemsHelper.GetLifeStageDateString(dateWithPrecision.value, precision);
        }
    }
    if (IsNullOrWhiteSpace(dateWithPrecision.text)) {
        date = null;
        dateText = "";
    }

    return { date, datePrecision, dateText };
};

export function MedicationEditForm(props: IMedicationEditFormProps): JSX.Element {
    const { getText } = useTranslation();
    const [startDateText, setStartDateText] = useState<string>(PatientMedicalItemsHelper.GetLifeStageDateString(
        props.medicationModel.Model.RefillStartDate,
        props.medicationModel.Model.StartDatePrecision
    ));

    const lastDosage = props.medicationModel.GetLastDosage();
    const dosageCount = props.medicationModel.Model.Dosages?.count() ?? 0;
    const isNarcotic = props.medicationModel.IsNarcotic();
    const lastDosageDuration = props.medicationModel.TicksToDays(lastDosage.Duration).toString();

    let totalDurationYears = props.estimateEndDate.getFullYear() - DateTime.nowDeprecated().getFullYear();
    let totalDurationMonths = props.estimateEndDate.getMonth() - DateTime.nowDeprecated().getMonth();
    let totalDurationDays = props.estimateEndDate.getDate() - DateTime.nowDeprecated().getDate();

    if (totalDurationDays < 0) {
        totalDurationMonths -= 1;
        totalDurationDays += 30; // Error here, depands on the month, maximum error = 2... acceptable
    }

    if (totalDurationMonths < 0) {
        totalDurationYears -= 1;
        totalDurationMonths += 12;
    }

    const yearString = (totalDurationYears !== 0 ? totalDurationYears + " " + getText("YearsLabel") + " " : "");
    const monthString = totalDurationMonths + " " + getText("MonthsLabel") + " ";
    const dayString = totalDurationDays + " " + getText("DaysLabel");

    let totalDurationIndicator = null;
    if (props.medicationModel.Model.RefillEndDate == null) {
        totalDurationIndicator = "∞ " + getText("DaysLabel");
    }
    else if (totalDurationYears != 0 || Math.abs(totalDurationMonths) > 1) {
        totalDurationIndicator = yearString + monthString;

        if (lastDosage.DisplayModeDurationUnit != MedicationDosageDisplayModeDurationUnit.Months) {
            totalDurationIndicator += dayString;
        }
    }

    const shouldShowAndCalculateDispensationQuantities = MedicationModel.shouldShowAndCalculateDispensationQuantities(isNarcotic);
    const shouldShowFirstDispensationQuantity = MedicationModel.shouldShowFirstDispensationQuantity(isNarcotic, dosageCount);
    const shouldShowRefillQuantity = MedicationModel.shouldShowRefillQuantity(isNarcotic, dosageCount, lastDosage.DisplayMode);
    const shouldShowSingleDosageQuantityInsteadOfDuration = MedicationModel.shouldShowSingleDosageQuantityInsteadOfDuration(dosageCount, lastDosage.DisplayMode);
    const isEmptyRx = props.medicationModel.Model.PrescriptionTab_IsEmptyRx;

    return (
        <div className="flex-container activemeditem">
            <div className="left-side"> {/* Main */}
                <div className={`flex-container meds ${isEmptyRx ? "" : styles.titleRow}`}>
                    {!isEmptyRx && (
                        <div className={`editablecell ${styles.medicationNameContainer}`}>
                            <span
                                onClick={() => props.onToggleEditPreviewMedicationModelClick()}
                                className={styles.medicationName}
                            >
                                {props.medicationModel.GetName()}
                            </span>
                        </div>
                    )}
                    {isEmptyRx && (
                        <div className="label">
                            <span>{getText("MedicationLabel") + ": "}</span>
                        </div>
                    )}
                    {isEmptyRx && (
                        <div className={`editablecell ${styles.medicationNameTextboxContainer}`}>
                            <TextboxControl
                                IsSmall={true}
                                id={"_medNameEmptyRx" + props.currentIdx}
                                className={`form-control input ${styles.medicationNameTextbox}`}
                                value={props.medicationModel.Model.Name}
                                onChange={(newText: string) => props.onMedicationNameChange(newText)}
                                dataCy="medicationEdit-medication"
                            />
                        </div>
                    )}
                    <div
                        className={cacheStore.ClinicSettings.EnableLongTermMedication
                            ? (isEmptyRx ? styles.emptyRxTopRightCheckboxesContainer : styles.topRightCheckboxesContainer)
                            : ""
                        }
                    >
                        {cacheStore.ClinicSettings.EnableLongTermMedication && (
                            <div className={`editablecell ${styles.topRightCheckboxContainer}`}>
                                <CheckboxControl
                                    id={`long-term_${props.medicationModel.Model.Id}`}
                                    checked={props.medicationModel.Model.IsLongTerm}
                                    labelClassName={styles.topRightCheckboxLabel}
                                    label={getText("LongTermLabel")}
                                    onChange={(isChecked, id, target) => props.onLongTermChange(isChecked, id, target)}
                                    dataCy="medicationEdit-longTermCheckbox"
                                />
                                <LongTermMedicationIcon />
                            </div>
                        )}
                        <div className={`editablecell ${styles.topRightCheckboxContainer}`}>
                            <CheckboxControl
                                checked={!props.medicationModel.Model.IsSubstituable}
                                className={styles.doNotSubstituteCheckbox}
                                labelClassName={styles.topRightCheckboxLabel}
                                label={getText("NotSubstituableLabel")}
                                fixTextSelectionInterference={props.fixTextSelectionInterferingWithCheckbox}
                                onChange={(isChecked: boolean, id: string, target?: number) => props.onDoNotSubstituteChange(isChecked, id, target)}
                            />
                        </div>
                    </div>
                </div>
                <div className="flex-container meds">
                    {!!props.medicationModel.PrescriptionTab_OriginalMedication && (
                        <div className={`editablecell ${styles.originalMedicationConatiner}`}>
                            <div className={`flex-container meds ${styles.originalMedicationSubContainer}`}>
                                {props.medicationModel.PrescriptionTab_OriginalMedication.Model.PrescriptionTab_PendingStatus == "2" && (
                                    <div>
                                        <span className={`glyphicon glyphicon-triangle-top ${styles.pendingStatus} ${styles.mr5}`} />
                                    </div>
                                )}
                                {props.medicationModel.PrescriptionTab_OriginalMedication.Model.PrescriptionTab_PendingStatus == "3" && (
                                    <div>
                                        <span className={`glyphicon glyphicon-triangle-bottom ${styles.pendingStatus} ${styles.mr5}`} />
                                    </div>
                                )}
                                <div>
                                    <span className={styles.pendingStatus}>{props.medicationModel.GetNameForChildMedication(false)}</span>
                                </div>
                                <div>
                                    <span className={styles.italicLabelWithMarginLeft}>{getText("DateOf")}</span>
                                </div>
                                <div>
                                    <span className={styles.italicLabelWithMarginLeft}>
                                        {moment(props.medicationModel.PrescriptionTab_OriginalMedication.Model.CeaseDate).format("YYYY-MM-DD") + ","}
                                    </span>
                                </div>
                                {props.medicationModel.PrescriptionTab_OriginalMedication.Model?.CeaseReason?.length > 0 && (
                                    <div className={styles.ceaseReasonContainer}>
                                        <span>
                                            {getText("ReasonLabel") + ": " + props.medicationModel.PrescriptionTab_OriginalMedication.Model.CeaseReason}
                                        </span>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </div>
                {!props.medicationModel.IsLoading && (
                    <div>
                        {lastDosage.IsFakeDosage != true && (
                            <div className="flex-container meds">
                                <div className="label"><span>{getText("RouteLabel")}</span></div>
                                <div className={`editablecell ${styles.routeContainer}`}>
                                    <Combobox
                                        id="_routeCombobox"
                                        className="sm"
                                        selectedCode={props.medicationModel.Model.Route}
                                        items={props.routes}
                                        onChange={(selectedCode: string) => props.onRouteChange(selectedCode)}
                                        HasEmptyItem={true}
                                        doInitialChangeOp={false}
                                        dataCy="medicationEdit-route"
                                    />
                                </div>
                            </div>
                        )}
                        {props.children}
                        <div className={`flex-container meds ${styles.alignFlexStart}`}>
                            <div className={`label ${styles.mt6}`}>
                                <span>{getText("InstructionDLabel")}</span>
                            </div>
                            <div className="editablecell">
                                <TextAreaResizable
                                    verticalResizeOnly
                                    id={"_insctruction_" + props.medicationModel.Model.Id}
                                    className={styles.instructionTextarea}
                                    minHeightPx={52}
                                    isDisabled={false}
                                    IsSmall
                                    resetTextOnIdChange={false}
                                    text={props.medicationModel.Model.Instruction}
                                    onChange={value => props.onInstructionChange(value)}
                                    dataCy="medication-specific-instruction"
                                />
                            </div>
                            <div className={`flex-container meds ${styles.addDosageContainer}`}>
                                <div className={`editablecell ${styles.addDosageSubContainer}`}>
                                    <HyperLinkControl
                                        text={getText("AddDosageLabel")}
                                        id={props.medicationModel.Model.Id}
                                        onChange={() => props.onAddDosageClick()}
                                        dataCy="medicationEdit-addDosage"
                                    />
                                </div>
                            </div>
                        </div>
                        {shouldShowFirstDispensationQuantity && (
                            <div className="flex-container meds">
                                <div className="label"><span>{getText("FirstDispensationLabel")}</span></div>
                                <div className="editablecell">
                                    <TextboxControl
                                        IsSmall
                                        id={`FirstDispensation_${props.currentIdx}`}
                                        value={props.medicationModel.Model.InitialRefillQuantity}
                                        className={`form-control input ${styles.initialRefillQuantityTextbox}`}
                                        onChange={(newText: string) => props.onInitialRefillQuantityChange(newText)}
                                    />
                                </div>
                            </div>
                        )}
                        {shouldShowAndCalculateDispensationQuantities && dosageCount === 1 && (
                            <div className="flex-container meds">
                                <div className="label">
                                    <span>{shouldShowSingleDosageQuantityInsteadOfDuration
                                        ? getText("QuantityLabel")
                                        : getText("DurationLabel")
                                    }
                                    </span>
                                </div>
                                <div className="editablecell">
                                    <TextboxControl
                                        IsSmall
                                        IsNumeric={!shouldShowSingleDosageQuantityInsteadOfDuration}
                                        id={`QuantityOrDuration_${props.currentIdx}`}
                                        value={shouldShowSingleDosageQuantityInsteadOfDuration
                                            ? lastDosage.DosageServedQuantity
                                            : lastDosage.Duration ? lastDosageDuration : ""
                                        }
                                        className={`form-control input ${styles.dosageOrQuantityTextbox}`}
                                        onChange={(newText: string) => props.onQuantityOrDurationChange(newText)}
                                    />
                                </div>
                                {!shouldShowSingleDosageQuantityInsteadOfDuration && (
                                    <div className="margin-sm-left">{getText("DaysLabel")}</div>
                                )}
                            </div>
                        )}
                        <div key="Renewals" className="flex-container meds">
                            <div className="label">
                                <span className={styles.renewalShortLabel}>
                                    {getText("RenewalShortLabel")}
                                </span>
                                <span />
                            </div>
                            <div className="editablecell">
                                {!isNarcotic && (
                                    <TextboxControl
                                        IsSmall
                                        isInteger
                                        max={1000}
                                        id={`Renewals_${props.currentIdx}`}
                                        className={`form-control input ${styles.renewalTextbox}`}
                                        value={props.numberOfRefill}
                                        onChange={(newText: string) => props.onRenewalChange(newText)}
                                        dataCy="medicationEdit-renewals"
                                    />
                                )}
                                {isNarcotic && <label className={styles.noRefillLabel}>{getText("NoRefillLabel")}</label>}
                            </div>
                        </div>
                        {shouldShowRefillQuantity && (
                            <div key="RefillQuantity" className="flex-container meds">
                                <div className="label">
                                    <span>{getText("RefillQuantityLabel")}</span>
                                </div>
                                <div className="editablecell">
                                    <TextboxControl
                                        IsSmall
                                        id={`RefillQuantity_${props.currentIdx}`}
                                        value={props.medicationModel.Model.RefillQuantity}
                                        className={`form-control input ${styles.refillQuantityTextbox}`}
                                        onChange={(newText: string) => props.onRefillQuantityChange(newText)}
                                    />
                                </div>
                            </div>
                        )}
                        {props.requiresTotalDuration && (
                            <div className="flex-container meds">
                                <div className="label"><span>{getText("TotalDurationLabel")}</span></div>
                                <div className="editablecell">
                                    <TextboxControl
                                        IsSmall={true}
                                        id={"_totalduration" + props.currentIdx}
                                        IsNumeric={true}
                                        value={props.totalDurationNumberOfDays.toString()}
                                        maxLength={7}
                                        className={`form-control input ${styles.totalDurationTextbox}`}
                                        onChange={(newText: string) => props.onTotalDurationChange(newText)}
                                    />
                                </div>
                                {(totalDurationYears != 0 || totalDurationMonths > 1) && (
                                    <div className={`editablecell ${styles.totalDurationIndicatorContainer}`}>
                                        <span>{totalDurationIndicator}</span>
                                    </div>
                                )}
                            </div>
                        )}
                        {cacheStore.ClinicSettings.EnablePrescriberDatesAndComplianceFields && (
                            <div className="flex-container meds">
                                <div className="label"><span>{getText("TreatmentStartLabel")}</span></div>
                                <TextboxControlWithDatePicker
                                    id="medicationStartDate"
                                    textValue={startDateText}
                                    dateValue={props.medicationModel.Model.RefillStartDate}
                                    width={110}
                                    onChange={datePrecision => {
                                        const result = onDateChange(datePrecision, startDateText);
                                        props.onStartDateChange(result.date ?? moment(Date.now()).toDate(), result.datePrecision ?? LifeStageDatePrecisionEnum.Day);
                                        setStartDateText(result.dateText);
                                    }}
                                />
                            </div>
                        )}
                        {cacheStore.ClinicSettings.EnableMedicationNotes && props.onInternalNoteChange && (
                            <div className={`flex-container meds ${styles.alignFlexStart}`}>
                                <div className={`label ${styles.mt6}`}>
                                    <span>{getText("MedicalSummary_Medication_InternalNoteLabel")}</span>
                                </div>
                                <div className="editablecell">
                                    <TextAreaResizable
                                        verticalResizeOnly
                                        className={styles.instructionTextarea}
                                        id={"_note_" + props.medicationModel.Model.Id}
                                        minHeightPx={52}
                                        IsSmall
                                        text={props.medicationModel.Model.InternalNote}
                                        onChange={value => props.onInternalNoteChange(value)}
                                        dataCy="medicationEdit-internalNote"
                                    />
                                </div>
                            </div>
                        )}

                        {props.isPreviewButtonVisible && (
                            <div className={`flex-container meds ${styles.previewButtonContainer}`}>
                                <div className="editablecell">
                                    <ButtonControl
                                        id={props.medicationModel.Model.Id}
                                        onClick={() => props.onToggleEditPreviewMedicationModelClick()}
                                        className="btn btn-primary"
                                        label={getText("PreviewLabel")}
                                        dataCy="medicationEdit-previewButton"
                                    />
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </div>
            <div className="right-side text-right">
                <div className="flex-container">
                    {props.medicationModel.IsLoading &&
                        <div className={`editablecell ${styles.loadingContainer}`}><span> {getText("LoadingLabel")} </span></div>
                    }
                    {props.isDeletable && (
                        <div
                            className="editablecell"
                            onClick={() => props.onRemoveMedicationClick()}
                        >
                            <img
                                alt="delete dosage"
                                className={`file-img ${styles.deleteIcon}`}
                                src={img_icon_delete}
                                aria-expanded="false"
                            />
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
